// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// Component imports
import { Row, Col, Typography, Space, Button, Divider } from 'antd';
import InfiniteScrollList from './InfiniteScrollList';
import NotificationCard from './NotificationCard';

// Misc imports
import { useReadAllNotificationsMutation, useFetchNotificationsQuery } from '../../store/notifications/notificationsApi';

const NotificationsListContainer = ({ isMobile = false, unreadNotificationCount, onNotificationClick }) => {
  const currentUser = useSelector((state) => state.auth.currentUser);
  const [canLoadMoreNotifications, setCanLoadMoreNotifications] = useState(false);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [readAllNotificationMutation] = useReadAllNotificationsMutation();
  const { orgId } = useParams();
  const userId = currentUser.id;
  const TAKE_SIZE = 20;

  const [paginationState, setPaginationState] = useState({
    skip: 0,
    take: TAKE_SIZE,
    showUnreadOnly: false
  });

  // we pass the page and pageSize as query params in the listing endpoing
  // and keep skip/take for the infinite scroll list to work.
  const page = Math.floor(paginationState.skip / paginationState.take);
  const pageSize = paginationState.take;

  const urlParams = {
    showUnreadOnly: paginationState.showUnreadOnly.toString(),
    userId: userId,
    page: page,
    pageSize: pageSize
  };

  const {
    currentData: notificationsResponse,
    isFetching: isFetchingNotifications,
    isLoading: isLoadingNotifications
  } = useFetchNotificationsQuery({
    orgId,
    urlParams
  });

  const notifications = notificationsResponse?.notifications || [];
  const notificationTotal = notificationsResponse?.total || 0;

  useEffect(() => {
    if (notifications && !isFetchingNotifications) {
      setFilteredNotifications(notifications);
      const canLoadMore = notifications.length < notificationTotal;
      if (canLoadMoreNotifications !== canLoadMore) {
        setCanLoadMoreNotifications(canLoadMore);
      }
    }
  }, [notifications, notificationTotal, isFetchingNotifications, canLoadMoreNotifications]);

  const getNextPage = (skip = 0, take = TAKE_SIZE) => {
    setPaginationState(prev => ({
      ...prev,
      skip,
      take
    }));
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await readAllNotificationMutation({ orgId, userId: currentUser.id });
      if (response.error) {
        console.error('Error while marking notifications as read:', response.error);
      }
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const toggleUnreadFilter = (showUnreadOnly) => {
    setPaginationState({
      skip: 0,
      take: TAKE_SIZE,
      showUnreadOnly
    });
  };

  const renderNotifications = () => (
    <Fragment>
      <div className="p-2" style={{ minWidth: isMobile ? '100%' : 415, maxWidth: isMobile ? '100%' : 415 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Typography.Text className="fs-xl fw-600">
              Notifications
            </Typography.Text>
          </Col>
          <Col>
            <Space>
              <Button
                type="text"
                shape="round"
                onClick={() => toggleUnreadFilter(false)}
              >
                All
              </Button>
              <Button
                shape="round"
                type="text"
                onClick={() => toggleUnreadFilter(true)}
              >
                Unread
              </Button>
            </Space>
          </Col>
        </Row>
        <Divider className="mt-4 mb-1" />
        <Row justify="end">
          <Col>
            <Button
              type="link"
              onClick={handleMarkAllAsRead}
              disabled={notificationTotal === 0 || unreadNotificationCount === 0}
            >
        Mark all as read
            </Button>
          </Col>
        </Row>
        <Row justify="center">
          <Col span={24}>
            <InfiniteScrollList
              dataSource={filteredNotifications}
              total={notificationTotal}
              renderItem={notification => (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  onNotificationClick={onNotificationClick}
                />
              )}
              loading={isLoadingNotifications}
              onLoadMore={getNextPage}
              loadMore={canLoadMoreNotifications}
              className="mt-2 overflow-y-auto overflow-x-clip scrollbar-custom scrollbar-custom-nohide"
              style={{ maxHeight: 500 }}
              height={15}
            />
          </Col>
        </Row>
      </div>
    </Fragment>
  );

  return renderNotifications();
};

NotificationsListContainer.propTypes = {
  isMobile: PropTypes.bool,
  unreadNotificationCount: PropTypes.number,
  onNotificationClick: PropTypes.func
};

export default NotificationsListContainer;
